import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DiscordOutlined, TikTokOutlined } from '@ant-design/icons';
import finishSignIn from '../utils/finishSignIn';
import { getLocalStorage, setLocalStorage } from '../utils/func';
import config from '../utils/config';
import { clearOAuthCallback, redirectToOAuth } from '../components/AuthButtonGoogleCustom/oauthConsent';
import { abortable, cancelNative, consumeAttempt, createProof, openNative, providerNames, readCallback, rememberAttempt,
  socialPlatform, socialRequest, validAuthorization, validNativeCallback } from './client';
import './index.scss';

const names = { discord: 'Discord', tiktok: 'TikTok' };
const icons = { discord: DiscordOutlined, tiktok: TikTokOutlined };
const labels = {
  en: 'Continue with {{provider}}', ru: 'Войти через {{provider}}', de: 'Weiter mit {{provider}}',
  es: 'Continuar con {{provider}}', fr: 'Continuer avec {{provider}}', hi: '{{provider}} से जारी रखें',
  it: 'Continua con {{provider}}', ja: '{{provider}}で続ける', ko: '{{provider}} 계정으로 계속',
  pt: 'Continuar com {{provider}}', tr: '{{provider}} ile devam et', zh: '使用{{provider}}继续',
};

export default function SocialAuthButtons({ disabled, onBusy }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(false);
  const pending = useRef(null);
  const controller = useRef(null);
  const processed = useRef(null);
  const platform = socialPlatform();
  const reset = useCallback(() => { pending.current = null; if (mounted.current) setBusy(false); }, []);

  useEffect(() => { onBusy?.(busy ? pending.current?.provider : null); }, [busy, onBusy]);
  useEffect(() => {
    mounted.current = true;
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 10000);
    socialRequest('/capabilities', null, abort.signal).then(data => {
      if (!mounted.current || abort.signal.aborted) return;
      setProviders(Array.isArray(data.providers) ? data.providers.filter(item => providerNames.includes(item.id)
        && Array.isArray(item.platforms) && item.platforms.includes(platform)) : []);
    }).catch(() => {}).finally(() => clearTimeout(timer));
    const restore = event => {
      if (!event.persisted || platform === 'ios') return;
      const attempt = pending.current;
      if (attempt?.state) consumeAttempt(attempt.provider, attempt.state);
      controller.current?.abort(); reset();
    };
    window.addEventListener('pageshow', restore);
    return () => {
      mounted.current = false; clearTimeout(timer); abort.abort(); controller.current?.abort();
      window.removeEventListener('pageshow', restore);
      if (pending.current?.state) {
        consumeAttempt(pending.current.provider, pending.current.state);
        if (platform === 'ios') cancelNative(pending.current.state);
      }
      pending.current = null;
    };
  }, [platform, reset]);

  const complete = useCallback(async address => {
    const callback = readCallback(address);
    if (!callback) return;
    const stamp = JSON.stringify(callback);
    if (processed.current === stamp) return;
    processed.current = stamp;
    if (callback.invalid) { setError('brand:authError'); return; }
    const { provider, state, ticket } = callback;
    const attempt = consumeAttempt(provider, state);
    if (!attempt) { setError('brand:authError'); if (pending.current?.state === state) reset(); return; }
    pending.current = { provider, state };
    setBusy(true); setError('');
    const abort = new AbortController(); controller.current = abort;
    const startingToken = getLocalStorage(config.token);
    const current = () => mounted.current && !abort.signal.aborted && pending.current?.state === state
      && getLocalStorage(config.token) === startingToken;
    const timer = setTimeout(() => abort.abort(), 25000);
    try {
      const data = await abortable(socialRequest(`/${provider}/complete`, { state, ticket, verifier: attempt.verifier }, abort.signal), abort.signal);
      if (current()) await abortable(finishSignIn(data, { navigate, i18n, isCurrent: current, registration: attempt.registration, signal: abort.signal }), abort.signal);
    } catch (cause) {
      if (mounted.current && pending.current?.state === state && cause.message !== 'AUTH_CANCELLED') setError('brand:authError');
    } finally { clearTimeout(timer); if (pending.current?.state === state) reset(); }
  }, [navigate, i18n, reset]);

  useEffect(() => {
    const address = window.location.href;
    if (!readCallback(address)) return;
    clearOAuthCallback(['social_provider', 'social_state', 'social_code', 'social_error']);
    complete(address);
  }, [location, complete]);

  const signIn = async provider => {
    if (disabled || pending.current || !providers.some(item => item.id === provider)) return;
    const operation = { provider };
    pending.current = operation; setBusy(true); setError('');
    const abort = new AbortController(); controller.current = abort;
    const timer = setTimeout(() => abort.abort(), 15000);
    try {
      const proof = await abortable(createProof(), abort.signal);
      if (!mounted.current || pending.current !== operation) return;
      if (abort.signal.aborted) throw Error('Sign-in preparation expired');
      const response = await abortable(socialRequest(`/${provider}/start`, { challenge: proof.challenge, platform, consent: true }, abort.signal), abort.signal);
      if (!mounted.current || pending.current !== operation) return;
      if (abort.signal.aborted) throw Error('Sign-in preparation expired');
      if (!validAuthorization(response.authorization_url, provider, response.state)) throw Error('Invalid provider URL');
      rememberAttempt(provider, response.state, proof.verifier);
      operation.state = response.state;
      const from = new URLSearchParams(window.location.search).get('from');
      setLocalStorage('login_from', from?.startsWith('/') && !from.startsWith('//') ? from : '/');
      clearTimeout(timer);
      if (platform === 'ios') {
        const result = await openNative(response.authorization_url, operation.state, provider);
        if (!mounted.current || pending.current !== operation) return;
        if (result.cancelled) { consumeAttempt(provider, operation.state); reset(); return; }
        if (!validNativeCallback(result.url, provider, operation.state)) throw Error('Invalid callback');
        await complete(result.url);
      } else redirectToOAuth(response.authorization_url);
    } catch {
      if (!mounted.current || pending.current !== operation) return;
      if (operation.state) consumeAttempt(provider, operation.state);
      setError('brand:authError'); reset();
    } finally { clearTimeout(timer); }
  };

  if (!providers.length && !error) return null;
  return <div className="social-auth-buttons">
    {error && <p className="error-message" role="alert">{t(error)}</p>}
    {providers.map(({ id }) => {
      const ProviderIcon = icons[id];
      return <button key={id} type="button" className={`button_border${busy && pending.current?.provider === id ? ' auth-button-busy' : ''}`}
        disabled={disabled || busy} aria-busy={busy && pending.current?.provider === id} onClick={() => signIn(id)}>
        <ProviderIcon className="social-auth-icon" aria-hidden="true" />
        <span className="social-auth-label">{(labels[i18n.language?.split('-')[0]] || labels.en).replace('{{provider}}', names[id])}</span>
      </button>;
    })}
  </div>;
}
